import { ccfRoutes } from '@app/ccf/ccf-routes';
import ReportPublishLoader from '@app/components/reports/components/report-publish-loader';
import {
  ASYNC_PUBLISH_RESPONSE_TIME,
  LOADER_DURATION,
} from '@app/components/reports/create-report/create-report-publish';
import { zodResolver } from '@hookform/resolvers/zod';
import { usePublishReport } from '@shared/api/hooks/ccf/activities/publish';
import {
  CompanyReportCategoryStatusStatusEnum,
  CompanyReportStatusEnum,
} from '@shared/api/types';
import Text from '@shared/components/content/text';
import GuideLayout from '@shared/components/guide-layout';
import Loader from '@shared/components/loader';
import { Button } from '@shared/components/ui/button';
import { Form } from '@shared/components/ui/form';
import { useToast } from '@shared/components/ui/use-toast';
import sentry from '@shared/services/sentry';
import { parseISO } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { generatePath, Outlet, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { useCCF } from '../ccf-context';
import { PublishReportContext } from './use-publish-report';
import { createDefaultValues, createSchema } from './utils';

const categoryStatuses = Object.values(
  CompanyReportCategoryStatusStatusEnum
).map((value) => value);

export default function ReportPublishLayout() {
  const navigate = useNavigate();
  const { report_uuid: reportUuid } = useParams();
  const { toast } = useToast();
  const [jobId, setJobId] = useState<string>();
  const [publishTime, setPublishTime] = useState<number>();
  const [isSyncPublishing, setIsSyncPublishing] = useState<boolean>(false);
  const {
    categories,
    status,
    reportStatus,
    lastPublishedAt,
    setCompanyReportPolling,
  } = useCCF();

  const { mutateAsync: publishReport, isLoading: isPublishing } =
    usePublishReport();
  const [isComplete, setIsComplete] = useState<boolean>(false);

  const viewReportList = useCallback(() => {
    const listUrl = ccfRoutes.COMPANY_REPORTS;
    navigate(listUrl, { replace: true });
  }, [navigate]);

  const viewResults = useCallback(() => {
    const resultsUrl = generatePath(ccfRoutes.REPORT_RESULT, {
      report_uuid: reportUuid,
    });
    navigate(resultsUrl, { replace: true });
  }, [navigate, reportUuid]);

  const handleFailedPublish = useCallback(() => {
    toast({
      title: 'Report publish failed',
      description:
        'There was an issue publishing your report. Please try again or get in touch if the issue persists.',
      variant: 'destructive',
    });
  }, [toast]);

  const publishSchema = categories
    ? createSchema(categories, categoryStatuses)
    : undefined;

  const form = useForm({
    resolver: publishSchema ? zodResolver(publishSchema) : undefined,
    defaultValues: categories ? createDefaultValues(categories) : {},
  });

  useEffect(() => {
    if (categories) {
      form.reset({
        ...createDefaultValues(categories),
      });
    }
  }, [form, categories]);

  const onSubmit = async (data: z.infer<ReturnType<typeof createSchema>>) => {
    try {
      const startTime = Date.now();
      setPublishTime(startTime);

      // Assume it's being handled sync if response isn't quick
      const syncPublishTimeout = setTimeout(() => {
        setIsSyncPublishing(true);
      }, ASYNC_PUBLISH_RESPONSE_TIME);

      const { jobId } = await publishReport({
        companyReportUuid: reportUuid as string,
        data: [
          ...Object.values(data).map((item: any) => ({
            activityType: item.activityType,
            status: item.status,
          })),
        ],
      });

      clearTimeout(syncPublishTimeout);

      if (jobId) {
        setJobId(jobId);
        setCompanyReportPolling(true);
        setIsSyncPublishing(false);
      } else {
        const elapsedTime = Date.now() - startTime;
        const remainingTime =
          LOADER_DURATION - elapsedTime > 0 ? LOADER_DURATION - elapsedTime : 0;

        setTimeout(() => {
          setIsComplete(true);
          viewResults();
        }, remainingTime);
      }
    } catch (error) {
      toast({
        title: 'Failed publishing report',
        description: 'Please try again or get in touch if the issue persists.',
        variant: 'destructive',
      });
      setIsSyncPublishing(false);
      sentry.log(error);
    }
  };

  useEffect(() => {
    if (!jobId || !publishTime || !lastPublishedAt) return;

    const newlyPublished = parseISO(lastPublishedAt).getTime() > publishTime;
    if (!newlyPublished) return;

    if (status === CompanyReportStatusEnum.published) {
      viewResults();
    } else if (status === CompanyReportStatusEnum.failed) {
      handleFailedPublish();
    }
  }, [
    jobId,
    publishTime,
    lastPublishedAt,
    status,
    viewResults,
    handleFailedPublish,
    setCompanyReportPolling,
  ]);

  const context: PublishReportContext = {
    onSubmit,
  };

  const showInitialLoader = isPublishing && !isSyncPublishing && !jobId;
  const showSyncLoader = isSyncPublishing && !jobId;
  const showAsyncLoader = !!jobId;
  const showForm = !showInitialLoader && !showSyncLoader && !showAsyncLoader;

  return (
    <GuideLayout
      pageName="Publish your report"
      onClosePath={generatePath(ccfRoutes.COMPANY_REPORT, {
        report_uuid: reportUuid,
      })}
      status={reportStatus}
    >
      <div className="flex flex-1 items-center justify-center">
        {showInitialLoader && <Loader size="sm" />}

        {showSyncLoader && (
          <ReportPublishLoader
            duration={LOADER_DURATION}
            forceComplete={isComplete}
          />
        )}
        {showAsyncLoader && (
          <div className="flex max-w-lg flex-col items-center space-y-8">
            <div className="flex flex-row items-center space-x-4">
              <div>
                <Loader size="sm" />
              </div>
              <Text variant="cardTitle" className="text-primary-700">
                Generating report
              </Text>
            </div>
            <Text className="text-center text-primary-700">
              Due to the size of this report, it may take a few minutes to
              publish. You can wait on this page and be automatically redirected
              once the report is ready, or leave and check in later on the
              progress from the reports page.
            </Text>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="bg-transparent hover:bg-transparent"
              onClick={viewReportList}
            >
              View company reports
            </Button>
          </div>
        )}
        {showForm && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <Outlet context={context} />
            </form>
          </Form>
        )}
      </div>
    </GuideLayout>
  );
}
